/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useState, useRef, useEffect } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';

// Runtime SDK
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';

// Components
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CardIcon, FavoriteIcon, LogoutIcon, MapPinIcon, OrdersIcon, ProfileIcon } from '@/components/icons';

// hooks
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';

// Lib
import { cn } from '@/lib/utils';
import { routes } from '@/route-paths';

interface UserMenuProps {
    isAuthenticated: boolean;
    trigger: ReactElement;
}

// Registered-shopper dropdown (Figma: Dropdown-Mi cuenta-SS)
const accountMenuItemClassName = cn(
    'flex items-center gap-2 rounded-lg p-[11px] text-sm leading-6 font-medium text-header-account-menu-foreground',
    'hover:bg-header-account-menu-hover-background transition-colors',
    'outline-none focus-visible:bg-header-account-menu-hover-background'
);

const accountMenuItems = [
    { to: routes.account, labelKey: 'accountMenu.profile', Icon: ProfileIcon },
    { to: routes.accountOrders, labelKey: 'accountMenu.orders', Icon: OrdersIcon },
    { to: routes.accountAddresses, labelKey: 'accountMenu.addresses', Icon: MapPinIcon },
    { to: routes.accountPaymentMethods, labelKey: 'accountMenu.cards', Icon: CardIcon },
    { to: routes.accountWishlist, labelKey: 'accountMenu.favorites', Icon: FavoriteIcon },
] as const;

export function UserMenu({ isAuthenticated, trigger }: UserMenuProps): ReactElement {
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const openedViaMouseRef = useRef(false);
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        openedViaMouseRef.current = true;
        setOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 150); // Small delay to allow moving from trigger to content
    };

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
        if (!newOpen) {
            if (openedViaMouseRef.current) {
                // Blur trigger when closing via mouse to prevent focus ring
                requestAnimationFrame(() => {
                    const activeElement = document.activeElement as HTMLElement;
                    if (activeElement?.closest('[data-slot="popover-trigger"]')) {
                        activeElement.blur();
                    }
                });
            }
            openedViaMouseRef.current = false;
        }
    };

    const handleOpenAutoFocus = (e: Event) => {
        if (openedViaMouseRef.current) {
            e.preventDefault();
        }
    };

    const handleCloseAutoFocus = (e: Event) => {
        if (openedViaMouseRef.current) {
            e.preventDefault();
        }
    };

    const handleMenuItemMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.focus();
    };

    // Common Popover props. Radix renders PopoverContent as role="dialog", so it needs an
    // accessible name (WCAG 4.1.2 / axe aria-dialog-name); reuse the trigger's label rather
    // than add a new key. The trigger's aria-haspopup="dialog" already announces the opener.
    const popoverContentProps = {
        className: 'w-64 p-0 overflow-hidden bg-background border-border',
        align: 'end' as const,
        sideOffset: 8,
        'aria-label': isAuthenticated ? tAccount('myAccount') : t('signIn'),
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onOpenAutoFocus: handleOpenAutoFocus,
        onCloseAutoFocus: handleCloseAutoFocus,
    };

    if (isAuthenticated) {
        return (
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    {trigger}
                </PopoverTrigger>
                <PopoverContent
                    {...popoverContentProps}
                    className="w-[210px] border-transparent bg-header-account-menu-background px-6 py-3 [--ui-radius:var(--radius-lg)] [--ui-shadow:0_2px_23px_rgb(0_0_0/0.07)]">
                    <div className="flex flex-col gap-3">
                        {accountMenuItems.map(({ to, labelKey, Icon }) => (
                            <Link
                                key={to}
                                to={to}
                                className={accountMenuItemClassName}
                                onMouseEnter={handleMenuItemMouseEnter}>
                                <Icon className="size-6 shrink-0" />
                                {t(labelKey)}
                            </Link>
                        ))}
                        <div className="h-px bg-header-account-menu-divider" />
                        <Form
                            method="post"
                            action={buildUrl({
                                to: '/logout',
                                urlConfig: config.url,
                                params: { siteId: siteRef, localeId: localeRef },
                            })}
                            className="w-full">
                            <button
                                type="submit"
                                data-testid="user-menu-logout"
                                className={cn(accountMenuItemClassName, 'w-full text-left cursor-pointer')}
                                onMouseEnter={handleMenuItemMouseEnter}>
                                <LogoutIcon className="size-6 shrink-0" />
                                {t('accountMenu.signOut')}
                            </button>
                        </Form>
                    </div>
                </PopoverContent>
            </Popover>
        );
    }

    // Guest user menu
    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {trigger}
            </PopoverTrigger>
            <PopoverContent {...popoverContentProps}>
                <div className="p-4 bg-muted/50 ">
                    <p className="text-sm text-muted-foreground mb-4">{t('menu.signInForBestExperience')}</p>
                    <Button asChild className="w-full mb-3 ">
                        <Link to={routes.login} onMouseEnter={handleMenuItemMouseEnter}>
                            {t('signIn')}
                        </Link>
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                        {t('menu.newCustomer')}{' '}
                        <Link
                            to={routes.signup}
                            className={cn(
                                'text-popover-foreground hover:underline px-1 py-0.5',
                                'outline-none focus-visible:bg-muted focus-visible:text-popover-foreground'
                            )}
                            onMouseEnter={handleMenuItemMouseEnter}>
                            {t('menu.createAccount')}
                        </Link>
                    </p>
                </div>
            </PopoverContent>
        </Popover>
    );
}
